import React from 'react';
import { Box } from '@mui/material';
import { useWatch } from 'react-hook-form';
import { FormInputText } from '../form';
import {
  CERTIFIED_STAMP_BORDER,
  CERTIFIED_STAMP_COLOR,
  CERTIFIED_STAMP_FONT_FAMILY,
  CERTIFIED_STAMP_COMPACT_LABEL,
  CERTIFIED_STAMP_FORM_BODY_FONT_SIZE,
  CERTIFIED_STAMP_FORM_CELL_PADDING,
  CERTIFIED_STAMP_FORM_COMPACT_FONT_SIZE,
  CERTIFIED_STAMP_FORM_COMPACT_HEIGHT,
  CERTIFIED_STAMP_FORM_COMPACT_PAD,
  CERTIFIED_STAMP_FORM_COMPACT_SIGN_MAX_WIDTH,
  CERTIFIED_STAMP_FORM_HEADER_FONT_SIZE,
  CERTIFIED_STAMP_FORM_HEADER_HEIGHT,
  CERTIFIED_STAMP_FORM_SIGN_MAX_WIDTH,
  CERTIFIED_STAMP_FORM_WIDTH,
  CERTIFIED_STAMP_HEADER_LABEL,
  CERTIFIED_STAMP_SIGN_MAX_WIDTH,
  CERTIFIED_STAMP_SIGN_OPACITY,
  STAMP_PDF_COMPACT_HEIGHT_PT,
  formatSupplierLine,
} from './certifiedStampSpec';

const rowSx = {
  px: CERTIFIED_STAMP_FORM_CELL_PADDING,
  py: CERTIFIED_STAMP_FORM_CELL_PADDING,
  fontSize: CERTIFIED_STAMP_FORM_BODY_FONT_SIZE,
  color: CERTIFIED_STAMP_COLOR,
  borderTop: CERTIFIED_STAMP_BORDER,
  lineHeight: 1.5,
  boxSizing: 'border-box',
};

const inlineInputSx = {
  flex: 1,
  minWidth: 0,
  '& .MuiInput-root': {
    fontSize: CERTIFIED_STAMP_FORM_BODY_FONT_SIZE,
    marginTop: 0,
    color: CERTIFIED_STAMP_COLOR,
    lineHeight: 1.5,
  },
  '& .MuiInput-input': {
    padding: 0,
    fontSize: CERTIFIED_STAMP_FORM_BODY_FONT_SIZE,
    color: CERTIFIED_STAMP_COLOR,
  },
  '& .MuiInput-underline:before': {
    borderBottomColor: 'rgba(102, 74, 160, 0.42)',
  },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    borderBottomColor: CERTIFIED_STAMP_COLOR,
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: CERTIFIED_STAMP_COLOR,
  },
};

const stampLabelColSx = {
  width: '5.6em',
  flex: '0 0 5.6em',
  minWidth: 0,
};

const stampColonSx = {
  flex: '0 0 auto',
  px: 0.5,
};

const stampValueColSx = {
  flex: 1,
  minWidth: 0,
};

const StampLabelValueRow = ({ label, value }) => (
  <Box sx={{ ...rowSx, display: 'flex', alignItems: 'baseline' }}>
    <Box sx={stampLabelColSx}>{label}</Box>
    <Box sx={stampColonSx}>:</Box>
    <Box sx={stampValueColSx}>{value}</Box>
  </Box>
);

const StampEditableRow = ({
  prefix,
  name,
  control,
  error,
  onChange,
}) => (
  <Box
    sx={{
      ...rowSx,
      display: 'flex',
      alignItems: 'baseline',
      gap: 0,
    }}
  >
    <Box component="span" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
      {prefix}
    </Box>
    <FormInputText
      name={name}
      control={control}
      variant="standard"
      fullWidth
      error={error}
      errorStyle={{ message: false, border: true }}
      onCustomChange={onChange}
      inputProps={{ 'aria-label': prefix.replace(/\s*:\s*$/, '') }}
      sx={inlineInputSx}
    />
  </Box>
);

/**
 * 원본대조필 스탬프 — PdfUtils.printCopyVerification 1열 레이아웃 근사.
 * 제출처·현장명만 밑줄 입력, 인감은 (인) 위 오버레이.
 * compact이면 헤더·인감만.
 */
const CertifiedStampPreview = ({
  control,
  signThumbnailUrl = '',
  emptyRecipient = false,
  emptySite = false,
  onChangeRecipient,
  onChangeSite,
  compact = false,
  templateEdit = false,
  readOnly = false,
  onChangeSupplierLabel,
  onChangeSupplier,
  onChangePhrase,
}) => {
  const supplierLabel = useWatch({ control, name: 'supplierLabel' });
  const supplier = useWatch({ control, name: 'supplier' });
  const phrase = useWatch({ control, name: 'phrase' });
  const compactHeight = CERTIFIED_STAMP_FORM_COMPACT_HEIGHT;
  // PDF처럼 인감이 헤더보다 큼 — 섹션 overflow:hidden 에 잘리지 않게 상단 여유
  const signOverhang = compact
    ? `${Math.max(0, (parseFloat(CERTIFIED_STAMP_SIGN_MAX_WIDTH) - STAMP_PDF_COMPACT_HEIGHT_PT) / 2)}pt`
    : `${Math.max(0, Math.ceil((CERTIFIED_STAMP_FORM_SIGN_MAX_WIDTH - CERTIFIED_STAMP_FORM_HEADER_HEIGHT) / 2))}px`;

  return (
    <Box sx={{ pt: signOverhang, overflow: 'visible', maxWidth: '100%' }}>
    <Box
      aria-label="원본대조필 날인정보"
      sx={{
        width: compact ? 'fit-content' : CERTIFIED_STAMP_FORM_WIDTH,
        maxWidth: '100%',
        border: CERTIFIED_STAMP_BORDER,
        boxSizing: 'border-box',
        fontFamily: CERTIFIED_STAMP_FONT_FAMILY,
        color: CERTIFIED_STAMP_COLOR,
        bgcolor: '#fff',
        overflow: 'visible',
      }}
    >
      {compact ? (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          height: compactHeight,
          boxSizing: 'border-box',
          fontSize: CERTIFIED_STAMP_FORM_COMPACT_FONT_SIZE,
          color: CERTIFIED_STAMP_COLOR,
          lineHeight: 1,
          overflow: 'visible',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: CERTIFIED_STAMP_FORM_COMPACT_PAD,
            whiteSpace: 'nowrap',
            boxSizing: 'border-box',
          }}
        >
          {CERTIFIED_STAMP_COMPACT_LABEL}
        </Box>
        <Box
          sx={{
            position: 'relative',
            width: compactHeight,
            flexShrink: 0,
            boxSizing: 'border-box',
            borderLeft: CERTIFIED_STAMP_BORDER,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'visible',
          }}
        >
          (인)
          {signThumbnailUrl ? (
            <Box
              component="img"
              src={signThumbnailUrl}
              alt="인감"
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                maxWidth: CERTIFIED_STAMP_FORM_COMPACT_SIGN_MAX_WIDTH,
                maxHeight: CERTIFIED_STAMP_FORM_COMPACT_SIGN_MAX_WIDTH,
                width: 'auto',
                height: 'auto',
                opacity: CERTIFIED_STAMP_SIGN_OPACITY,
                pointerEvents: 'none',
                userSelect: 'none',
              }}
            />
          ) : null}
        </Box>
      </Box>
      ) : (
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: CERTIFIED_STAMP_FORM_HEADER_HEIGHT,
          px: CERTIFIED_STAMP_FORM_CELL_PADDING,
          boxSizing: 'border-box',
          fontSize: CERTIFIED_STAMP_FORM_HEADER_FONT_SIZE,
          color: CERTIFIED_STAMP_COLOR,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          textAlign: 'center',
          overflow: 'visible',
        }}
      >
        {CERTIFIED_STAMP_HEADER_LABEL.replace(' (인)', '')}
        {' '}
        <Box
          component="span"
          sx={{
            position: 'relative',
            display: 'inline-block',
            minWidth: '1.6em',
          }}
        >
          (인)
          {signThumbnailUrl ? (
            <Box
              component="img"
              src={signThumbnailUrl}
              alt="인감"
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                maxWidth: CERTIFIED_STAMP_FORM_SIGN_MAX_WIDTH,
                maxHeight: CERTIFIED_STAMP_FORM_SIGN_MAX_WIDTH,
                width: 'auto',
                height: 'auto',
                opacity: CERTIFIED_STAMP_SIGN_OPACITY,
                pointerEvents: 'none',
                userSelect: 'none',
              }}
            />
          ) : null}
        </Box>
      </Box>
      )}

      {!compact && (
        <>
      {templateEdit ? (
        <Box sx={{ ...rowSx, display: 'flex', alignItems: 'baseline' }}>
          <FormInputText
            name="supplierLabel"
            control={control}
            variant="standard"
            errorStyle={{ message: false, border: true }}
            onCustomChange={onChangeSupplierLabel}
            inputProps={{ 'aria-label': '제공사 라벨', disabled: readOnly }}
            sx={{ ...inlineInputSx, ...stampLabelColSx }}
          />
          <Box sx={stampColonSx}>:</Box>
          <FormInputText
            name="supplier"
            control={control}
            variant="standard"
            errorStyle={{ message: false, border: true }}
            onCustomChange={onChangeSupplier}
            inputProps={{ 'aria-label': '제공사', disabled: readOnly }}
            sx={{ ...inlineInputSx, ...stampValueColSx }}
          />
        </Box>
      ) : (
      <Box sx={{ ...rowSx, wordBreak: 'break-all' }}>
        {formatSupplierLine(supplierLabel, supplier)}
      </Box>
      )}

      {templateEdit ? (
        <>
          <StampLabelValueRow label="제출처" value="제출처명" />
          <StampLabelValueRow label="현장명" value="현장명" />
        </>
      ) : (
        <>
      <StampEditableRow
        prefix="제출처 : "
        name="recipient"
        control={control}
        error={emptyRecipient}
        onChange={onChangeRecipient}
      />

      <StampEditableRow
        prefix="현장명 : "
        name="site"
        control={control}
        error={emptySite}
        onChange={onChangeSite}
      />
        </>
      )}

      {templateEdit ? (
        <Box sx={rowSx}>
          <FormInputText
            name="phrase"
            control={control}
            variant="standard"
            multiline
            maxRows={5}
            errorStyle={{ message: false, border: true }}
            onCustomChange={onChangePhrase}
            inputProps={{ 'aria-label': '문구', disabled: readOnly }}
            sx={inlineInputSx}
          />
        </Box>
      ) : (
      <Box
        sx={{
          ...rowSx,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}
      >
        {phrase || ''}
      </Box>
      )}
        </>
      )}
    </Box>
    </Box>
  );
};

export default CertifiedStampPreview;
